import { useCallback, useEffect, useState } from 'react';
import { UmsatzWert } from '@/components/UmsatzSperre';
import { toast } from 'sonner';
import { Dumbbell } from 'lucide-react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { PaketKarte } from '@/components/CoachingKarte';
import CoachingCheckins from '@/components/CoachingCheckins';

/* P1 Coaching (12.10.2026): alle Pakete, das Kontingent und der Trainer-Anteil je Monat (nur Bericht für die
   Lohnabrechnung). Verkauft wird am Lead oder Mitglied (Karte „P1 Coaching"). */
const euro = (v) => `${Number(v || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const FILTER = [['bezahlt,laufend', 'Laufend'], ['offen', 'Wartet auf Zahlung'], ['abgeschlossen,verfallen', 'Beendet'], ['storniert,erstattet', 'Storniert'], ['', 'Alle']];
const feld = 'h-9 rounded-md border border-input bg-background px-2 text-sm';
const ccLaden = () => api.get('/coaching/checkins');
const ccAntworten = (cid, text) => api.post(`/coaching/checkins/${cid}/antwort`, { text });

export default function CoachingPaketePage() {
  const { user } = useAuth();
  const leitung = user?.role === 'admin' || user?.role === 'manager';
  const [status, setStatus] = useState(FILTER[0][0]);
  const [coach, setCoach] = useState('');
  const [d, setD] = useState(null);
  const [plaetze, setPlaetze] = useState('');
  const [monat, setMonat] = useState(() => new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Berlin' }).slice(0, 7));
  const [ab, setAb] = useState(null);

  const lade = useCallback(async () => {
    try {
      const r = await api.get('/coaching/pakete', { params: { status: status || undefined, coach: coach || undefined } });
      const voll = await Promise.all(r.data.pakete.map((p) => (['bezahlt', 'laufend'].includes(p.status) ? api.get(`/coaching/pakete/${p.id}`).then((x) => x.data.paket).catch(() => p) : p)));
      setD({ ...r.data, pakete: voll }); setPlaetze(String(r.data.kontingent.plaetze));
    } catch { toast.error('Pakete nicht geladen'); }
  }, [status, coach]);
  useEffect(() => { lade(); }, [lade]);
  useEffect(() => { if (leitung) api.get('/coaching/abrechnung', { params: { monat } }).then((r) => setAb(r.data)).catch(() => setAb(null)); }, [monat, leitung, d]);

  const kontingent = async () => {
    try { await api.put('/coaching/kontingent', { plaetze: Number(plaetze) }); toast.success('Kontingent gespeichert'); lade(); }
    catch (e) { toast.error(typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : 'Nicht gespeichert'); }
  };

  return (
    <div className="space-y-5 max-w-5xl" data-testid="coaching-seite">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2"><Dumbbell className="h-6 w-6 text-marke" /> P1 Coaching</h1>
        <p className="text-sm text-muted-foreground mt-1">Pakete aus Coach-Terminen und Trainingsplan. Verkauft wird in der Lead-Ansicht über die Karte „P1 Coaching“.</p>
      </div>
      <CoachingCheckins laden={ccLaden} antworten={ccAntworten} />

      {d && (
        <div className="grid gap-3 sm:grid-cols-3">
          <Card><CardContent className="pt-5"><p className="text-xs text-muted-foreground">Plätze belegt</p><p className="text-3xl font-bold tabular-nums">{d.kontingent.belegt}<span className="text-base font-medium text-muted-foreground"> von {d.kontingent.plaetze}</span></p><p className="text-xs text-muted-foreground mt-1">{d.kontingent.frei} frei</p></CardContent></Card>
          <Card><CardContent className="pt-5"><p className="text-xs text-muted-foreground">In dieser Ansicht</p><p className="text-3xl font-bold tabular-nums">{d.pakete.length}</p><p className="text-xs text-muted-foreground mt-1"><UmsatzWert wert={d.summen.umsatz} format={euro} kurz /> Paketwert (bezahlt)</p></CardContent></Card>
          <Card><CardContent className="pt-5 space-y-2"><p className="text-xs text-muted-foreground">Kontingent laufender Pakete</p>
            {leitung ? <div className="flex gap-2"><Input className="h-9 w-24" inputMode="numeric" value={plaetze} onChange={(e) => setPlaetze(e.target.value.replace(/\D/g, ''))} aria-label="Plätze" /><Button size="sm" variant="outline" disabled={!plaetze || Number(plaetze) === d.kontingent.plaetze} onClick={kontingent}>Speichern</Button></div>
              : <p className="text-sm">{d.kontingent.plaetze} Plätze · ändern kann die Leitung</p>}
            <p className="text-[11px] text-muted-foreground">Nur sagen, was stimmt: die Zahl aus der echten Trainer-Kapazität.</p></CardContent></Card>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {FILTER.map(([wert, text]) => <Button key={text} size="sm" variant={status === wert ? 'default' : 'outline'} onClick={() => setStatus(wert)}>{text}</Button>)}
        <select className={`${feld} ml-auto`} aria-label="Coach" value={coach} onChange={(e) => setCoach(e.target.value)}>
          <option value="">alle Coaches</option>{(d?.trainer || []).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
      </div>
      <div className="space-y-3">
        {d && d.pakete.length === 0 && <p className="text-sm text-muted-foreground">Keine Pakete in dieser Ansicht.</p>}
        {(d?.pakete || []).map((p) => <PaketKarte key={p.id} paket={p} trainer={d.trainer} rolle={user?.role} onGeaendert={lade} mitName />)}
      </div>

      {leitung && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base flex flex-wrap items-center gap-3">Trainer-Anteil
            <input type="month" className={feld} aria-label="Monat" value={monat} onChange={(e) => setMonat(e.target.value)} />
            <span className="text-xs font-normal text-muted-foreground">35 % vom Netto-Paketpreis, je wahrgenommenem Termin · nur Bericht für die Lohnabrechnung, keine Auszahlung</span></CardTitle></CardHeader>
          <CardContent>
            {!ab || ab.coaches.length === 0 ? <p className="text-sm text-muted-foreground">In diesem Monat wurde kein Coaching-Termin wahrgenommen.</p> : (
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs text-muted-foreground"><th className="py-1 font-normal">Coach</th><th className="font-normal text-right">Termine</th><th className="font-normal text-right">Stunden</th><th className="font-normal text-right">Anteil</th></tr></thead>
                <tbody>
                  {ab.coaches.map((c) => <tr key={c.coach} className="border-t border-border/50"><td className="py-1.5 font-medium">{c.coach}</td><td className="text-right tabular-nums">{c.termine}</td><td className="text-right tabular-nums">{String(c.stunden).replace('.', ',')}</td><td className="text-right font-bold tabular-nums">{euro(c.anteil)}</td></tr>)}
                  <tr className="border-t border-border"><td className="py-1.5 font-semibold">Summe</td><td /><td /><td className="text-right font-bold tabular-nums">{euro(ab.summe)}</td></tr>
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
